import { ChangeDetectionStrategy, Component, computed, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  AccountSelectionService,
  OrganizationAccountGroupService,
  OrganizationUsersService,
} from "@front/m19-services";
import { TranslocoDirective } from "@jsverse/transloco";
import { ActivateDspAutomationCtaComponent } from "@m19-board/shared/activate-dsp-automation-cta/activate-dsp-automation-cta.component";
import { FeaturePitchOverlayComponent } from "@m19-board/overlay/feature-pitch-overlay/feature-pitch-overlay.component";
import { OverlayRule } from "@m19-board/overlay/overlay-service/overlay-rules";
import { combineLatest, map } from "rxjs";

@Component({
  selector: "app-dsp-bidder-off-overlay",
  imports: [TranslocoDirective, FeaturePitchOverlayComponent, ActivateDspAutomationCtaComponent],
  template: `
    <app-feature-pitch-overlay *transloco="let t" feature="dsp" [title]="t('overlays.bidder_off_title')">
      @if (organization(); as organization) {
        <app-activate-dsp-automation-cta
          [accountMarketplace]="accountMarketplace()"
          [organization]="organization"
          [isAdminOrOwner]="isAdminOrOwner()"
          [label]="t('overlays.activate_ads_automation')"
        />
      }
    </app-feature-pitch-overlay>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspBidderOffOverlayComponent {
  static readonly rule = OverlayRule.DSP_BIDDER_OFF;

  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly organizationAccountGroupService = inject(OrganizationAccountGroupService);
  private readonly organizationUsersService = inject(OrganizationUsersService);

  protected readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });

  protected readonly organization = toSignal(
    combineLatest([
      this.organizationAccountGroupService.allOrganizationAccountGroups$,
      this.accountSelectionService.accountGroupSelection$,
    ]).pipe(map(([orgs, accountGroup]) => orgs.find((org) => org.id == accountGroup.organizationId))),
  );

  private readonly adminOrganizationIds = toSignal(this.organizationUsersService.adminOrganizationIds$);

  protected readonly isAdminOrOwner = computed(
    () => this.adminOrganizationIds()?.has(this.accountMarketplace().resourceOrganizationId!) ?? false,
  );
}
